import { useState } from "react";

const contextExamples = [
  {
    label: "社内情報を調べる",
    contexts: [
      ["Luna", "依頼の目的・最新の進捗"],
      ["Worker", "関連資料・検索方法・情報の利用ルール"],
      ["Reviewer", "回答案・参照した根拠・公開先"],
    ],
  },
  {
    label: "成果物を共有する",
    contexts: [
      ["Luna", "追加の依頼・共有作業の進捗"],
      ["Worker", "成果物・受取人・共有方法と条件"],
      ["Reviewer", "実行予定の操作・確定した送信先・適用ルール"],
    ],
  },
] as const;

function PolicyContextDiagram() {
  const [selectedExample, setSelectedExample] = useState<(typeof contextExamples)[number]>(contextExamples[0]);
  return <figure className="marketing-mechanism-figure marketing-policy-context">
    <div className="marketing-policy-database">
      <strong>Basket Policy</strong>
      <span>AIエージェントへの指示を構造化した正本</span>
      <ul><li>ユーザー・役務</li><li>状況・環境</li><li>能力・利用目的</li><li>情報・操作の扱い</li><li>本人の接続・権限</li><li>確認・代替手段</li></ul>
    </div>
    <p className="marketing-context-sources">＋ 関連する業務仕様・知識・ツールの正本</p>
    <p className="marketing-flow-arrow" aria-hidden="true">↓</p>
    <div className="marketing-context-selector">
      <strong>Adaptive Context</strong>
      <span>現在の行動・状況・担当の責任に合わせて選ぶ</span>
    </div>
    <fieldset className="marketing-context-demo">
      <legend>行動に応じて渡す情報の例</legend>
      <div className="marketing-context-switch">{contextExamples.map((example) => <button key={example.label} type="button" aria-pressed={selectedExample === example} onClick={() => setSelectedExample(example)}>{example.label}</button>)}</div>
      <div className="marketing-context-outputs" aria-live="polite" aria-atomic="true">{selectedExample.contexts.map(([role, context]) => <div key={role}><strong>{role}</strong><p>{context}</p></div>)}</div>
    </fieldset>
    <figcaption>同じ大きな正本から、各エージェントへ必要十分な最小コンテキストを渡します。行動や状況が変わるたびに、追加・更新・除外します。</figcaption>
  </figure>;
}

function TriadDiagram() {
  return <figure className="marketing-mechanism-figure marketing-agent-diagram">
    <div className="marketing-agent-user"><strong>利用者</strong><span>Slackで依頼・相談・追加の指示</span></div>
    <p className="marketing-agent-conversation"><span aria-hidden="true">↕</span> 会話・進捗へのすばやい応答</p>
    <div className="marketing-agent-front"><strong>Luna</strong><span>会話のフロント ＋ Observer</span><p>受け答え・進捗の実況・実行中の監視</p></div>
    <div className="marketing-agent-signals">
      <p><span>↓ 作業依頼・追加指示</span><span>↑ 進捗・実行状況</span></p>
      <p><span>↓ 監視の記録</span></p>
    </div>
    <div className="marketing-agent-workflow">
      <div className="marketing-agent-worker"><strong>Worker</strong><span>高性能モデル</span><p>調査・制作・ツール実行</p><p>方法や再試行を自律判断</p></div>
      <div className="marketing-agent-review-arrow" aria-hidden="true">→</div>
      <div className="marketing-agent-reviewer"><strong>Reviewer</strong><span>独立した審査</span><p>回答・操作・根拠を照合</p><p>通過か差し戻しかを判断</p></div>
    </div>
    <div className="marketing-agent-decisions">
      <p><strong>↶ 差し戻し</strong><span>Workerが修正し、再び審査へ</span></p>
      <p><strong>↓ 通過</strong><span>確認した回答を届ける・操作を実行する</span></p>
    </div>
    <figcaption>この三者にもAdaptive Contextで異なる文脈を渡します。Lunaは現在の会話と進捗、Workerは実務に必要な情報、Reviewerは審査対象と直接適用するルールに集中します。</figcaption>
  </figure>;
}

export function SystemMechanisms() {
  return <section className="marketing-system-mechanisms marketing-shell" aria-label="Basketの設計">
    <article>
      <div className="marketing-mechanism-copy">
        <h2>Basket PolicyとAdaptive Context</h2>
        <h3>エージェントへの指示を定義する、Basket Policy</h3>
        <p>状況、ユーザー、役務、環境ごとに、AIエージェントがどう振る舞うべきかを定義した、指示のデータベースにあたる大きな正本です。使える能力とその用途、情報の扱い方、操作や共有の条件、確認が必要な場面、安全な代替手段まで構造化して管理します。</p>
        <p>「自分のアカウント・権限で使う」ことも、このPolicyの一部です。誰の依頼を、どの接続先で、どこまで実行できるかを定義します。実行側はSlackの本人とサービスごとのアカウントを照合し、認証情報をAIへ渡さずに操作します。</p>
        <h3>行動に適応する、最小コンテキスト</h3>
        <p><strong>Adaptive Context（適応型コンテキスト）</strong>は、その大きな正本から、AIエージェントが今の行動に必要な最小コンテキストを得る仕組みです。ユーザー、場所、目的、現在の行動、利用する能力、担当エージェントの責任に応じて、渡す情報を動的に構成します。</p>
        <p>AIが読む前に必要な部分を選び、行動が変われば追加・更新・除外します。必要な指示と判断材料を保ちながら、毎回すべてを読む負担や無関係な情報の混入を抑える。この組み合わせが、速さと複雑な仕事への対応を支えます。</p>
      </div>
      <PolicyContextDiagram />
    </article>
    <article>
      <div className="marketing-mechanism-copy">
        <h2>Luna・Worker・Reviewerの三すくみ</h2>
        <h3>Luna：人と話し、進捗を伝え、監視するフロント</h3>
        <p>Slackの通常会話で、最初に人の言葉を受け取っているのは、軽量・高速モデルの<strong>Luna</strong>です。受け答えや追加依頼への対応、進捗の実況を担いながら、Workerの実行状況を観測するObserverも兼ねます。重い作業の完了を待たずに、会話を続けられます。</p>
        <h3>Worker：高性能モデルが、方法を考えて実行する</h3>
        <p>実務はSolなどの高性能モデルを使うWorkerへ。調査、制作、ツールの組み合わせ、再試行や代替経路を、目的に沿って比較的自由に選びます。実行方法を細かな固定分岐に閉じ込めず、LLMの推論力と応用力を使います。</p>
        <h3>Reviewer：独立した視点で、通過か差し戻しかを決める</h3>
        <p>回答や外部へ影響する操作は、必要な審査へ進みます。ReviewerはWorkerの会話を引き継がず、対象の回答・操作、根拠、Lunaの観測内容、適用するPolicyを照合します。問題があれば理由を添えて差し戻し、Workerが修正。通過した内容を届け、操作を実行します。</p>
      </div>
      <TriadDiagram />
      <p className="marketing-architecture-value">この役割分担へ、Adaptive Contextがそれぞれに必要な最小コンテキストを渡します。本人確認や認証情報の隔離などは実行基盤が保証し、目的の解釈や進め方はLLMへ任せることで、新しい依頼への応用力と、モデルの進化を生かせる構成にしています。</p>
    </article>
  </section>;
}
